import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createSelector } from 'reselect'
import { Routes, Route } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import './App.scss'

import { layoutTypes } from './constants/layout'
import { setCredentials, initializeComplete } from './store/slices/authSlice'

// Import Routes all
import { authProtectedRoutes, publicRoutes } from './routes'

// Import all middleware
import Authmiddleware from './routes/route'

// layouts Format
import VerticalLayout from './components/VerticalLayout/'
import HorizontalLayout from './components/HorizontalLayout/'
import NonAuthLayout from './components/NonAuthLayout'

const getLayout = (layoutType) => {
  let Layout = VerticalLayout
  switch (layoutType) {
    case layoutTypes.VERTICAL:
      Layout = VerticalLayout
      break
    case layoutTypes.HORIZONTAL:
      Layout = HorizontalLayout
      break
    default:
      break
  }
  return Layout
}

const App = () => {
  const dispatch = useDispatch()
  const { isInitialized, accessToken } = useSelector(state => state.auth)
  
  // Check authentication by attempting to refresh token from HttpOnly cookie
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Try to refresh token using HttpOnly cookie
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api'}/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // Send HttpOnly cookie
        })
        
        if (response.ok) {
          const data = await response.json()
          dispatch(setCredentials({ user: data.user, accessToken: data.accessToken }))
        } else {
          // No valid session, mark initialization complete
          dispatch(initializeComplete())
        }
      } catch (error) {
        // No valid session, mark initialization complete
        dispatch(initializeComplete())
      }
    }

    // Only initialize if not already initialized
    if (!isInitialized) {
      initializeAuth()
    }
  }, [dispatch, isInitialized])

  // Periodically refresh token to keep session alive
  useEffect(() => {
    if (!isInitialized || !accessToken) return

    const refreshInterval = setInterval(async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api'}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        })
        
        if (response.ok) {
          const data = await response.json()
          dispatch(setCredentials({ user: data.user, accessToken: data.accessToken }))
        }
      } catch (error) {
        console.error('Token refresh failed:', error)
      }
    }, 14 * 60 * 1000) // Refresh every 14 minutes (before 15min expiry)

    return () => clearInterval(refreshInterval)
  }, [dispatch, isInitialized, accessToken])

  const LayoutProperties = createSelector(
    state => state.Layout,
    layout => ({
      layoutType: layout.layoutType,
    })
  )

  const { layoutType } = useSelector(LayoutProperties)

  const Layout = getLayout(layoutType)

  return (
    <React.Fragment>
      <Routes>
        {publicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={<NonAuthLayout>{route.component}</NonAuthLayout>}
            key={idx}
            exact={true}
          />
        ))}

        {authProtectedRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <Authmiddleware>
                <Layout>{route.component}</Layout>
              </Authmiddleware>
            }
            key={idx}
            exact={true}
          />
        ))}
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </React.Fragment>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

export default App
